<script setup>
// ACESSO-01 (L9b): as exceções de uma pessoa, no modal de editar agente, com a chave ACESSO_GRANULAR ligada (quadro 6 do
// mock v3, SPEC S5). Cada uma diz o que muda, até quando e por quê: o + (soma, teal) ou o − (tira, rubi), "Também pode
// …" ou "Não pode …", e em cinza o prazo e o motivo. A vencida, que já não vale, fica apagada e diz "Vencida em …" até
// alguém removê-la.
//
// - Ver, criar e remover são do `people:manage`, que quem mostra o componente confere. O seletor só oferece o escopo que
//   se concede, que não é travado e que quem concede tem (R6, R7); o `Acesso::Concessao` confere por cima.
// - O motivo é obrigatório, de 1 a 200 caracteres. O prazo é um dia, que a API recebe como AAAA-MM-DD, e vale até as
//   23:59 dele em São Paulo.
// - O limite de 20 em vigor, a repetida e a do outro sentido voltam do servidor como 422, e a recusa como 403: o
//   formulário mostra a frase dele.
// - Depois de criar e de remover, a lista volta do servidor, na ordem dele. A cada carga, o componente avisa quantas
//   estão em vigor e quantas venceram (`em-vigor`), para a lista de agentes dizer "1 exceção" ou "1 exceção vencida"
//   sem esperar o Salvar do modal.
// - Criada ou removida uma, o título diz "Salvo. Já vale para a pessoa.": o "Cancelar" do modal não a desfaz (revisão
//   de desenho, Task 9.6). O formulário tem os botões à esquerda, junto dos campos, e o "Cancelar" dele é discreto:
//   eram dois "Cancelar" iguais, um sobre o outro, com o do modal. O × de cada uma diz qual exceção remove.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAcesso } from 'dashboard/composables/useAcesso';
import AcessoAPI from 'dashboard/api/acesso';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { fraseDaFalha, segurarOEnter } from './acessoDaPessoa';

const props = defineProps({
  // O id do usuário, como em /agents/:id.
  pessoaId: { type: Number, required: true },
});

const emit = defineEmits(['emVigor']);

const MOTIVO_MAXIMO = 200;
const vazia = () => ({ sentido: 'soma', escopo: '', motivo: '', prazo: '' });

const { t, locale } = useI18n();
const store = useStore();
const { pode } = useAcesso();
const catalogo = useMapGetter('acesso/catalogo');

const excecoes = ref(null);
const erro = ref('');
const formAberto = ref(false);
const nova = ref(vazia());
const salvando = ref(false);
const erroDoForm = ref('');
const salvo = ref(false);

// O dia de hoje em São Paulo, o primeiro que o prazo aceita, como o campo de data o escreve.
const hoje = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
}).format(new Date());

const formatoDoDia = computed(
  () =>
    new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    })
);
const dia = prazo => formatoDoDia.value.format(new Date(`${prazo}T00:00:00Z`));

// O rótulo do escopo começa com maiúscula ("Publicar flows"), e aqui entra no meio da frase.
const noMeioDaFrase = rotulo =>
  rotulo.charAt(0).toLocaleLowerCase() + rotulo.slice(1);
const frase = excecao => {
  const rotulo = noMeioDaFrase(excecao.rotulo);
  return excecao.sentido === 'soma'
    ? t('AGENT_MGMT.ACESSO.EXCECOES.SOMA', { rotulo })
    : t('AGENT_MGMT.ACESSO.EXCECOES.TIRA', { rotulo });
};
const situacao = excecao => {
  if (!excecao.prazo) return t('AGENT_MGMT.ACESSO.EXCECOES.SEM_PRAZO');
  return excecao.vencida
    ? t('AGENT_MGMT.ACESSO.EXCECOES.VENCIDA_EM', { dia: dia(excecao.prazo) })
    : t('AGENT_MGMT.ACESSO.EXCECOES.ATE', { dia: dia(excecao.prazo) });
};
const corDoIcone = excecao => {
  if (excecao.vencida) return 'text-n-slate-10';
  return excecao.sentido === 'soma' ? 'text-n-teal-11' : 'text-n-ruby-11';
};

const sentidos = computed(() => [
  { value: 'soma', label: t('AGENT_MGMT.ACESSO.EXCECOES.FORM.SOMA') },
  { value: 'tira', label: t('AGENT_MGMT.ACESSO.EXCECOES.FORM.TIRA') },
]);
// Os escopos do catálogo, por módulo: só o que se concede, não é travado e quem concede tem.
const gruposDeEscopos = computed(() => {
  const { modulos = [], escopos = [] } = catalogo.value || {};
  return modulos
    .map(modulo => ({
      label: modulo.rotulo,
      options: escopos
        .filter(
          escopo =>
            escopo.modulo === modulo.chave &&
            escopo.concedivel &&
            !escopo.travado &&
            pode(escopo.nome)
        )
        .map(escopo => ({ value: escopo.nome, label: escopo.rotulo })),
    }))
    .filter(grupo => grupo.options.length);
});
const escopoEscolhido = computed(() =>
  catalogo.value?.escopos.find(escopo => escopo.nome === nova.value.escopo)
);
const motivo = computed(() => nova.value.motivo.trim());
const podeSalvar = computed(
  () =>
    !!nova.value.escopo &&
    motivo.value.length > 0 &&
    motivo.value.length <= MOTIVO_MAXIMO &&
    !salvando.value
);

const carregar = async () => {
  try {
    const { data } = await AcessoAPI.excecoes(props.pessoaId);
    excecoes.value = data.excecoes;
    erro.value = '';
    const vencidas = data.excecoes.filter(excecao => excecao.vencida).length;
    emit('emVigor', data.em_vigor, vencidas);
  } catch (falha) {
    erro.value = fraseDaFalha(
      falha,
      t('AGENT_MGMT.ACESSO.EXCECOES.ERRO_AO_CARREGAR')
    );
  }
};

const abrirForm = () => {
  nova.value = vazia();
  erroDoForm.value = '';
  formAberto.value = true;
};

const fecharForm = () => {
  formAberto.value = false;
};

const salvar = async () => {
  if (!podeSalvar.value) return;
  salvando.value = true;
  erroDoForm.value = '';
  const { sentido, escopo, prazo } = nova.value;
  try {
    await AcessoAPI.criarExcecao(props.pessoaId, {
      escopo,
      sentido,
      motivo: motivo.value,
      ...(prazo && { prazo }),
    });
    fecharForm();
    salvo.value = true;
    await carregar();
  } catch (falha) {
    erroDoForm.value = fraseDaFalha(
      falha,
      t('AGENT_MGMT.ACESSO.EXCECOES.ERRO_AO_SALVAR')
    );
  } finally {
    salvando.value = false;
  }
};

const remover = async excecao => {
  try {
    await AcessoAPI.apagarExcecao(props.pessoaId, excecao.id);
    salvo.value = true;
    await carregar();
  } catch (falha) {
    salvo.value = false;
    useAlert(
      fraseDaFalha(falha, t('AGENT_MGMT.ACESSO.EXCECOES.ERRO_AO_REMOVER'))
    );
  }
};

onMounted(() => {
  store.dispatch('acesso/carregarCatalogo').catch(() => {});
  carregar();
});
</script>

<template>
  <section class="flex flex-col" data-excecoes @keydown.enter="segurarOEnter">
    <div class="flex flex-wrap items-baseline justify-between gap-x-3">
      <h3 class="mb-0 text-sm font-medium leading-7 text-n-slate-12">
        {{ t('AGENT_MGMT.ACESSO.EXCECOES.TITULO') }}
      </h3>
      <p
        class="flex items-center gap-1 mb-0 text-xs text-n-slate-11"
        aria-live="polite"
        data-salvo
      >
        <template v-if="salvo">
          <span
            class="i-lucide-check size-3.5 shrink-0 text-n-teal-11"
            aria-hidden="true"
          />
          <span class="font-semibold text-n-slate-12">
            {{ t('AGENT_MGMT.ACESSO.SALVO') }}
          </span>
          {{ t('AGENT_MGMT.ACESSO.JA_VALE') }}
        </template>
      </p>
    </div>
    <p v-if="erro" class="py-2 mb-0 text-sm text-n-slate-11" data-erro>
      {{ erro }}
    </p>
    <p v-else-if="!excecoes" class="py-2 mb-0 text-sm text-n-slate-11">
      {{ t('AGENT_MGMT.ACESSO.EXCECOES.CARREGANDO') }}
    </p>
    <template v-else>
      <p
        v-if="!excecoes.length"
        class="py-2 mb-0 text-sm border-t border-n-weak text-n-slate-11"
        data-nenhuma
      >
        {{ t('AGENT_MGMT.ACESSO.EXCECOES.NENHUMA') }}
      </p>
      <div
        v-for="excecao in excecoes"
        :key="excecao.id"
        class="grid grid-cols-[1rem_minmax(0,1fr)_1.5rem] items-start gap-2.5 py-2.5 border-t border-n-weak"
        data-excecao
      >
        <span
          class="mt-0.5 size-4"
          :class="[
            excecao.sentido === 'soma' ? 'i-lucide-plus' : 'i-lucide-minus',
            corDoIcone(excecao),
          ]"
          aria-hidden="true"
          data-icone
        />
        <span class="flex flex-col min-w-0">
          <span
            class="text-sm font-medium break-words"
            :class="excecao.vencida ? 'text-n-slate-11' : 'text-n-slate-12'"
            data-frase
          >
            {{ frase(excecao) }}
          </span>
          <span class="text-xs break-words text-n-slate-11" data-situacao>
            <span
              v-if="excecao.vencida"
              class="font-semibold text-n-slate-12"
              data-vencida
            >
              {{ situacao(excecao) }}
            </span>
            <template v-else>{{ situacao(excecao) }}</template>
            {{ excecao.motivo }}
          </span>
        </span>
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-x"
          :aria-label="
            t('AGENT_MGMT.ACESSO.EXCECOES.REMOVER', { frase: frase(excecao) })
          "
          data-remover
          @click="remover(excecao)"
        />
      </div>

      <div
        v-if="formAberto"
        class="flex flex-col gap-3 pt-3 border-t border-n-weak"
        data-form
      >
        <div class="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-2">
          <Select
            v-model="nova.sentido"
            :options="sentidos"
            :aria-label="t('AGENT_MGMT.ACESSO.EXCECOES.FORM.SENTIDO')"
            class="!w-full"
            data-sentido
          />
          <Select
            v-model="nova.escopo"
            :groups="gruposDeEscopos"
            :placeholder="
              t('AGENT_MGMT.ACESSO.EXCECOES.FORM.ESCOPO_PLACEHOLDER')
            "
            :aria-label="t('AGENT_MGMT.ACESSO.EXCECOES.FORM.ESCOPO')"
            class="!w-full"
            data-escopo
          />
        </div>
        <p
          v-if="escopoEscolhido"
          class="mb-0 text-xs text-n-slate-11"
          data-frase-do-escopo
        >
          {{ escopoEscolhido.frase }}
        </p>
        <Input
          v-model="nova.motivo"
          :label="t('AGENT_MGMT.ACESSO.EXCECOES.FORM.MOTIVO')"
          :placeholder="t('AGENT_MGMT.ACESSO.EXCECOES.FORM.MOTIVO_PLACEHOLDER')"
          custom-input-class="placeholder:!text-n-slate-11 dark:placeholder:!text-n-slate-11"
          :maxlength="MOTIVO_MAXIMO"
          data-motivo
          @enter="salvar"
        />
        <div class="flex flex-col gap-1">
          <Input
            v-model="nova.prazo"
            type="date"
            :min="hoje"
            :label="t('AGENT_MGMT.ACESSO.EXCECOES.FORM.PRAZO')"
            data-prazo
          />
          <p class="mb-0 text-xs text-n-slate-11">
            {{ t('AGENT_MGMT.ACESSO.EXCECOES.FORM.PRAZO_AJUDA') }}
          </p>
        </div>
        <p
          v-if="erroDoForm"
          class="mb-0 text-sm text-n-ruby-11"
          role="alert"
          data-erro-do-form
        >
          {{ erroDoForm }}
        </p>
        <div class="flex justify-start gap-2">
          <Button
            type="button"
            size="sm"
            :label="t('AGENT_MGMT.ACESSO.EXCECOES.FORM.SALVAR')"
            :disabled="!podeSalvar"
            :is-loading="salvando"
            data-salvar
            @click="salvar"
          />
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="sm"
            :label="t('AGENT_MGMT.ACESSO.EXCECOES.FORM.CANCELAR')"
            data-cancelar
            @click="fecharForm"
          />
        </div>
      </div>
      <div v-else class="pt-2.5 border-t border-n-weak">
        <Button
          type="button"
          variant="link"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          :label="t('AGENT_MGMT.ACESSO.EXCECOES.ADICIONAR')"
          data-adicionar
          @click="abrirForm"
        />
      </div>
    </template>
  </section>
</template>
